import { useEffect, useMemo } from "react";
import Icon from "../components/Icon";
import { Avatar, Badge, BtnPrimary, Barra } from "../components/Atoms";
import HistorialReciente from "../components/widgets/HistorialReciente";
import DonutChart from "../components/widgets/DonutChart";
import BarraFiltros from "../components/filtros/BarraFiltros";
import { usePrefsFiltros } from "../components/filtros/usePrefsFiltros";
import { PJ_ESTADOS_PROYECTO, PJ_PRIOS, PR_CLR, PR_BG } from "../constants";
import { fmtHours, ringClr, puedeCrearProyectos, colorUsuario } from "../utils";
import { calendarioVsTrabajo } from "../kpisProyecto";
import { clicable } from "../components/ClicableTeclado.jsx";

const inS = { padding: "6px 9px", borderRadius: 8, border: "1px solid var(--border)",
  fontSize: 12, fontFamily: "var(--font)", background: "var(--surface)", color: "var(--text)" };

// Que se puede filtrar aqui. El COMO (cuales se ven, con cuales se arranca, si
// se recuerdan) lo decide el usuario desde el panel de la barra.
const CAMPOS = [
  { key: "q",             tipo: "texto", label: "Buscar", placeholder: "Buscar proyecto..." },
  { key: "estado",        label: "Estado",       icono: "flag" },
  { key: "prioridad",     label: "Prioridad",    icono: "exclamation-triangle-fill" },
  { key: "equipo",        label: "Departamento", icono: "diagram-3" },
  { key: "encargados",    label: "Encargados",   icono: "person-fill",
    buscar: "Buscar persona...", sinCoincidencias: "Nadie coincide", ordenar: true },
  { key: "participantes", label: "Participantes", icono: "people-fill",
    buscar: "Buscar persona...", sinCoincidencias: "Nadie coincide", ordenar: true },
  { key: "fechas",        tipo: "rango", desde: "desde", hasta: "hasta",
    label: "Rango de fechas", title: "Solapa con este rango" },
  // Apagado por defecto y oculto de la barra hasta que alguien lo pida desde el
  // panel: archivar es raro, y un boton permanente para algo que se mira dos
  // veces al año es ruido en la barra del resto de los dias.
  { key: "archivados", tipo: "conmutador", label: "Incluir archivados",
    porDefectoVisible: false,
    title: "Enseña tambien los proyectos archivados, para consultarlos o recuperarlos" },
];

// Sin configuracion propia se sigue arrancando por los proyectos activos, que
// es lo que la pagina hacia antes de que el filtrado fuera configurable.
const ARRANQUE = { estado: ["activo"] };

function TarjetaProyecto({ p, onAbrir, festivos = {} }) {
  const est = PJ_ESTADOS_PROYECTO.find(e => e.key === p.estado) || PJ_ESTADOS_PROYECTO[0];
  const prog = Number(p.progreso_efectivo || 0);
  const tareas = Number(p.tareas || 0);

  // Avance frente al calendario consumido. Se compara contra progreso_efectivo,
  // que es el mismo numero que ya pinta el donut de esta tarjeta: el detalle usa
  // el avance de esfuerzo y puede dar un veredicto distinto, asi que el title lo
  // dice. Sale null en los permanentes y en los que no tienen fechas, y entonces
  // la linea no se pinta: mejor ausencia que un guion en todas las tarjetas.
  const cal = calendarioVsTrabajo(p, prog, festivos);

  // Horas imputadas frente al plan. `plan_horas` lo resuelve el backend: la
  // estimacion del proyecto o, si no la hay, la suma de la de sus pasos.
  const plan    = p.plan_horas ?? null;
  const reales  = Number(p.horas_reales || 0);
  const pctPlan = plan ? Math.round((reales / plan) * 100) : null;
  const planDe  = p.estimacion_min
    ? "Horas imputadas frente al plan del proyecto"
    : "Horas imputadas frente a la suma de las estimaciones de los pasos";
  const miembros = p.miembros || [];
  const encargados = miembros.filter(m => m.rol === "encargado");
  // Se muestran antes los encargados: son a quien se acude.
  const visibles = [...encargados, ...miembros.filter(m => m.rol !== "encargado")].slice(0, 4);

  return (
    <div className="card-hover fu" {...clicable(() => onAbrir(p), { label: `Abrir proyecto ${p.name || p.nombre || ""}`.trim() })}
      style={{ background: "var(--surface)", border: "1px solid var(--border)",
        borderRadius: 13, padding: 14, borderTop: `3px solid ${p.color || "#3b82f6"}`,
        display: "flex", flexDirection: "column", gap: 10, minHeight: 190 }}>

      <div style={{ display: "flex", alignItems: "flex-start", gap: 9 }}>
        {p.icon && (
          <div style={{ width: 30, height: 30, borderRadius: 8, flexShrink: 0,
            background: (p.color || "#3b82f6") + "18", display: "flex",
            alignItems: "center", justifyContent: "center" }}>
            <Icon name={p.icon} size={15} color={p.color || "#3b82f6"} />
          </div>
        )}
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 13.5, color: "var(--text)",
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {p.name}
          </div>
          <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 2,
            display: "flex", gap: 6, flexWrap: "wrap" }}>
            <span style={{ color: est.color, fontWeight: 700 }}>{est.label}</span>
            {p.team_name && <span>· {p.team_name}</span>}
            {!p.can_edit && (
              <span title="Solo lectura: no puedes reorganizar este proyecto">· solo lectura</span>
            )}
          </div>
        </div>
        {/* El avance se dice una vez, aqui: el anillo, con su cifra al pasar el
            raton y para el lector de pantalla. Antes se repetia en un chip. */}
        <span title={`${prog}% completado`} style={{ display: "inline-flex", flexShrink: 0 }}>
          <DonutChart size={40} stroke={6} descripcion={`${prog}% completado`}
            data={[{ value: prog, color: ringClr(prog) },
                   { value: Math.max(0, 100 - prog), color: "var(--bg)" }]} />
        </span>
      </div>

      {p.description && (
        <div style={{ fontSize: 11.5, color: "var(--text3)", lineHeight: 1.45,
          overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical" }}>
          {p.description}
        </div>
      )}

      <div style={{ marginTop: "auto", display: "flex", flexDirection: "column", gap: 8 }}>

        {/* La barra es lo hecho; la rayita, donde deberia ir a estas alturas del
            calendario. Dos numeros en un solo trazo. */}
        {cal && (
          <div className="kpi-pair"
            title={`Trabajo hecho (${prog}%) frente al calendario ya consumido (${Math.round(cal.esperado)}%). No cuenta pasos vencidos: eso es el desvio de plazo.`}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 6, fontSize: 11 }}>
              <span style={{ color: "var(--text3)" }}>Calendario vs trabajo</span>
              <span style={{ flex: 1 }} />
              {/* Sin monoespaciar: DM Mono se reserva para las columnas de horas
                  y para las etiquetas del lienzo. Un veredicto es prosa. */}
              <span style={{ fontWeight: 700, color: cal.color }}>{cal.etiqueta}</span>
            </div>
            <Barra valor={prog} color={ringClr(prog)} alto={5} marca={cal.esperado} />
          </div>
        )}

        <div style={{ display: "flex", gap: 5, flexWrap: "wrap", alignItems: "center" }}>
          {tareas > 0 && (
            <Badge label={`${p.completadas || 0}/${tareas} pasos`}
              color="var(--text2)" bg="var(--surface2)" />
          )}
          {p.bloqueadas > 0 && (
            <Badge label={`${p.bloqueadas} bloq.`} color="#ef4444" bg="#fef2f2" />
          )}
          {p.vencidas > 0 && (
            <Badge label={`${p.vencidas} vencidas`} color="#b91c1c" bg="#fee2e2" />
          )}
          {p.prioridad && p.prioridad !== "Media" && (
            <Badge label={p.prioridad} color={PR_CLR[p.prioridad]} bg={PR_BG[p.prioridad]} />
          )}
          {/* Un archivado solo aparece si se ha pedido verlo, pero cuando lo hace
              tiene que distinguirse de los demas a la primera: si no, se opera
              sobre el creyendo que esta vivo. */}
          {p.archived && (
            <Badge label="Archivado" color="#a16207" bg="#fefce8" />
          )}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8,
          justifyContent: "space-between" }}>
          <div style={{ display: "flex" }}>
            {visibles.map((m, i) => (
              <div key={m.user_id + m.rol} style={{ marginLeft: i ? -6 : 0 }}
                title={`${m.name} · ${m.rol}`}>
                <Avatar name={m.name} size={22}
                  color={m.rol === "encargado" ? "var(--accent)" : "var(--text3)"} />
              </div>
            ))}
            {miembros.length > 4 && (
              <div style={{ marginLeft: -6, width: 22, height: 22, borderRadius: "50%",
                background: "var(--surface2)", border: "1.5px solid var(--border)",
                fontSize: 11, fontWeight: 700, color: "var(--text3)", display: "flex",
                alignItems: "center", justifyContent: "center" }}>
                +{miembros.length - 4}
              </div>
            )}
          </div>
          <div style={{ fontSize: 11, color: "var(--text3)", fontFamily: "var(--mono)",
            textAlign: "right" }} title={planDe}>
            {/* Horas reales del cuaderno: es el dato que nadie tenia que reportar
                dos veces. Un guion, no un 0, cuando no hay ninguna. Y frente al
                plan cuando lo hay: sin plan se dice, en vez de fingir un 0%. */}
            <div>
              {reales ? fmtHours(reales) : "—"}
              {plan
                ? <span style={{ color: pctPlan > 100 ? "var(--red-text)" : "var(--text3)" }}>
                    {" / "}{fmtHours(plan)} · {pctPlan}%
                  </span>
                : <span style={{ color: "var(--text3)" }}>{" · "}Sin plan</span>}
            </div>
            {/* Sin fecha de fin no hay linea de calendario: se dice aqui, donde iria
                la fecha, y no en un chip mas. */}
            <div>{p.end_date || "Permanente"}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ProyectosPage({
  proyectos = [], cargando, users = [], teams = [], currentUser, festivos = {},
  historial = [], onCargarHistorial, onAbrir, onNuevo, onFiltros,
}) {
  const prefs = usePrefsFiltros("proyectos", CAMPOS, ARRANQUE);
  const { valores: f, hayFiltro, limpiar } = prefs;

  const opciones = useMemo(() => {
    // Un color por persona, el mismo que usan las demas pantallas: con todos los
    // avatares en gris la inicial repetida (tres "A" seguidas) no distinguia a
    // nadie y el avatar solo ocupaba sitio.
    const gente = users.map(u => ({
      value: String(u.id), label: u.name, avatar: true,
      color: colorUsuario(u.id, users),
    }));
    return {
      estado:        PJ_ESTADOS_PROYECTO.map(e => ({ value: e.key, label: e.label })),
      prioridad:     PJ_PRIOS,
      equipo:        teams.map(t => ({ value: String(t.id), label: t.name })),
      encargados:    gente,
      participantes: gente,
    };
  }, [teams, users]);

  // El backend solo sabe filtrar por UN estado y UN equipo, asi que solo se le
  // manda la condicion cuando el usuario ha elegido exactamente uno; con varios
  // se le pide todo y la criba fina la hace la lista de aqui abajo. Es lo que
  // permite que todos los filtros sean de varias opciones sin tocar la API.
  const unico = (v) => (v.length === 1 ? v[0] : "");
  useEffect(() => {
    onFiltros?.({
      estado: unico(f.estado), equipo: unico(f.equipo),
      desde: f.desde, hasta: f.hasta, q: f.q,
      // Este solo lo sabe el servidor: un proyecto archivado ni siquiera llega
      // al cliente si no se pide, asi que no se puede cribar aqui abajo.
      archivados: f.archivados,
    });
  }, [f.estado, f.equipo, f.desde, f.hasta, f.q, f.archivados]); // eslint-disable-line

  const lista = useMemo(() => proyectos.filter(p => {
    const dentro = (sel, valor) => !sel.length || sel.some(s => String(s) === String(valor));
    if (!dentro(f.estado, p.estado))       return false;
    if (!dentro(f.prioridad, p.prioridad)) return false;
    if (!dentro(f.equipo, p.team_id))      return false;
    const conPapel = (sel, esRol) => !sel.length || (p.miembros || [])
      .some(m => esRol(m.rol) && sel.some(i => String(i) === String(m.user_id)));
    if (!conPapel(f.encargados,    r => r === "encargado")) return false;
    if (!conPapel(f.participantes, r => r !== "encargado")) return false;
    return true;
  }), [proyectos, f.estado, f.prioridad, f.equipo, f.encargados, f.participantes]);

  return (
    <div>
      <BarraFiltros campos={CAMPOS} prefs={prefs} opciones={opciones}
        derecha={<>
          {/* OJO con lo que cuenta: `proyectos` ya viene cribado por el backend
              (onFiltros le manda estado, equipo, fechas y texto), asi que el
              "de M" solo refleja la criba fina que hace el cliente, no el total
              que hay en la base de datos. */}
          <span style={{ fontSize: 11.5, color: "var(--text3)" }}
            title="Proyectos que se estan viendo">
            {lista.length}{lista.length !== proyectos.length ? ` de ${proyectos.length}` : ""}
            {lista.length === 1 ? " proyecto" : " proyectos"}
          </span>
          <HistorialReciente historial={historial} onCargar={onCargarHistorial} />
          {puedeCrearProyectos(currentUser) && (
            <BtnPrimary onClick={onNuevo}>
              <Icon name="plus-lg" size={11} color="#fff" />Nuevo proyecto
            </BtnPrimary>
          )}
        </>} />

      {cargando && !proyectos.length && (
        <div style={{ padding: 40, textAlign: "center", color: "var(--text3)", fontSize: 13 }}>
          Cargando proyectos...
        </div>
      )}

      {!cargando && !lista.length && (
        <div style={{ padding: "48px 20px", textAlign: "center" }}>
          <Icon name="diagram-3" size={30} color="var(--text3)" />
          <div style={{ fontWeight: 700, fontSize: 15, marginTop: 12, color: "var(--text2)" }}>
            {hayFiltro ? "Ningún proyecto encaja con el filtro" : "Todavía no hay proyectos"}
          </div>
          <div style={{ fontSize: 12.5, color: "var(--text3)", marginTop: 6, lineHeight: 1.6 }}>
            {hayFiltro
              ? "Prueba a quitar alguna condicion."
              : puedeCrearProyectos(currentUser)
                ? "Crea el primero para empezar a planificar pasos, plazos y responsables."
                : "Cuando te asignen uno aparecerá aquí."}
          </div>
          {hayFiltro && (
            <button className="btn" onClick={limpiar}
              style={{ ...inS, cursor: "pointer", marginTop: 14 }}>Limpiar filtros</button>
          )}
        </div>
      )}

      {!!lista.length && (
        <div style={{ display: "grid", gap: 12,
          gridTemplateColumns: "repeat(auto-fill,minmax(276px,1fr))" }}>
          {lista.map(p => <TarjetaProyecto key={p.id} p={p} onAbrir={onAbrir} festivos={festivos} />)}
        </div>
      )}
    </div>
  );
}
